import React, { useState, useEffect } from 'react';
import { 
  FileText, Download, Calendar, Filter, TrendingUp, 
  DollarSign, Users, Building2, PieChart, BarChart3,
  Printer, RefreshCw, ChevronRight, Eye, Table as TableIcon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { db } from '../../../lib/db';
import { formatUSDCurrency } from '../../../lib/usPayrollCalculations';
import { STATE_NAMES } from '../../../lib/usPayrollData';
import { formatDate } from '../../../lib/dateUtils';
import { exportToCSV } from '../../../lib/export';
import { toast } from 'sonner';

const US_REPORT_TYPES = [
  { id: 'payroll_register', name: 'Payroll Register', description: 'Detailed payroll run summary', icon: TableIcon },
  { id: 'tax_summary', name: 'Tax Summary', description: 'Federal and state tax breakdown', icon: Building2 },
  { id: 'employee_earnings', name: 'Employee Earnings', description: 'Individual earnings by employee', icon: Users },
  { id: 'department_summary', name: 'Department Summary', description: 'Payroll costs by department', icon: PieChart },
  { id: 'ytd_summary', name: 'YTD Summary', description: 'Year-to-date totals', icon: TrendingUp },
  { id: 'quarterly_941', name: 'Quarterly 941', description: 'Federal quarterly tax report', icon: FileText }
];

export default function USPayrollReports() {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [selectedReport, setSelectedReport] = useState('payroll_register');

  // Filters
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [selectedPayGroup, setSelectedPayGroup] = useState('all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedQuarter, setSelectedQuarter] = useState('1');

  // Reference data
  const [payGroups, setPayGroups] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [payrollItems, setPayrollItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [compensation, setCompensation] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!loading) {
      generateReport();
    }
  }, [selectedReport, dateRange, selectedPayGroup, selectedDepartment, selectedQuarter, loading]);

  const loadData = async () => {
    try {
      const [groups, runs, items, users, compData] = await Promise.all([
        db.payGroups.toArray(),
        db.payrollRuns.toArray(),
        db.payrollRunItems.toArray(),
        db.users.toArray(),
        db.employeeCompensation.toArray()
      ]);

      setPayGroups(groups);
      setPayrollRuns(runs);
      setPayrollItems(items);
      setEmployees(users);
      setCompensation(compData);

      // Extract unique departments
      const depts = [...new Set(users.map(u => u.profile?.department).filter(Boolean))];
      setDepartments(depts);

    } catch (error) {
      console.error('Error loading report data:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async () => {
    setGenerating(true);

    try {
      const startDate = new Date(dateRange.start).getTime();
      const endDate = new Date(dateRange.end).getTime() + 86400000;

      let filteredItems = payrollItems.filter(item => 
        item.createdAt >= startDate && item.createdAt <= endDate
      );

      if (selectedPayGroup !== 'all') {
        const groupRuns = payrollRuns.filter(r => r.payGroupId === selectedPayGroup).map(r => r.id);
        filteredItems = filteredItems.filter(item => groupRuns.includes(item.payrollRunId));
      }

      if (selectedDepartment !== 'all') {
        const deptEmployees = employees.filter(e => e.profile?.department === selectedDepartment).map(e => e.id);
        filteredItems = filteredItems.filter(item => deptEmployees.includes(item.userId));
      }

      const enrichedItems = filteredItems.map(item => {
        const emp = employees.find(e => e.id === item.userId);
        const comp = compensation.find(c => c.userId === item.userId);
        return {
          ...item,
          employee: emp,
          compensation: comp,
          employeeName: `${emp?.profile?.firstName || ''} ${emp?.profile?.lastName || ''}`.trim(),
          department: emp?.profile?.department || 'Unassigned',
          workState: comp?.workState || ''
        };
      });

      let data = null;

      switch (selectedReport) {
        case 'payroll_register':
          data = generatePayrollRegister(enrichedItems);
          break;
        case 'tax_summary':
          data = generateTaxSummary(enrichedItems);
          break;
        case 'employee_earnings':
          data = generateEmployeeEarnings(enrichedItems);
          break;
        case 'department_summary':
          data = generateDepartmentSummary(enrichedItems);
          break;
        case 'ytd_summary':
          data = generateYTDSummary(enrichedItems);
          break;
        case 'quarterly_941':
          data = generateQuarterly941(enrichedItems);
          break;
        default:
          data = null;
      }

      setReportData(data);
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const generatePayrollRegister = (items) => {
    const totals = items.reduce((acc, item) => ({
      grossPay: acc.grossPay + (item.grossPay || 0),
      federalTax: acc.federalTax + (item.federalTax || 0),
      stateTax: acc.stateTax + (item.stateTax || 0),
      socialSecurity: acc.socialSecurity + (item.socialSecurity || 0),
      medicare: acc.medicare + (item.medicare || 0),
      preTaxDeductions: acc.preTaxDeductions + (item.preTaxDeductions || 0),
      netPay: acc.netPay + (item.netPay || 0)
    }), {
      grossPay: 0, federalTax: 0, stateTax: 0, socialSecurity: 0,
      medicare: 0, preTaxDeductions: 0, netPay: 0
    });

    return {
      type: 'payroll_register',
      title: 'Payroll Register',
      items,
      totals
    };
  };

  const generateTaxSummary = (items) => {
    const federalTotal = items.reduce((sum, item) => sum + (item.federalTax || 0), 0);
    const ssTotal = items.reduce((sum, item) => sum + (item.socialSecurity || 0), 0);
    const medicareTotal = items.reduce((sum, item) => sum + (item.medicare || 0), 0);
    const grossWages = items.reduce((sum, item) => sum + (item.grossPay || 0), 0);

    const stateGroups = {};
    items.forEach(item => {
      const state = item.workState || 'Unknown';
      if (!stateGroups[state]) {
        stateGroups[state] = { employees: new Set(), wages: 0, tax: 0 };
      }
      stateGroups[state].employees.add(item.userId);
      stateGroups[state].wages += item.grossPay || 0;
      stateGroups[state].tax += item.stateTax || 0;
    });

    return {
      type: 'tax_summary',
      title: 'Tax Summary Report',
      federal: {
        grossWages,
        incomeTax: federalTotal,
        socialSecurity: ssTotal,
        medicare: medicareTotal,
        totalFICA: ssTotal + medicareTotal,
        employerFICA: ssTotal + medicareTotal,
        total941Liability: federalTotal + (ssTotal * 2) + (medicareTotal * 2)
      },
      states: Object.entries(stateGroups).map(([state, data]) => ({
        state,
        stateName: STATE_NAMES[state] || state,
        employees: data.employees.size,
        wages: data.wages,
        tax: data.tax
      })),
      grandTotal: federalTotal + ssTotal + medicareTotal + ssTotal + medicareTotal + 
                  Object.values(stateGroups).reduce((sum, s) => sum + s.tax, 0)
    };
  };

  const generateEmployeeEarnings = (items) => {
    const employeeGroups = {};
    items.forEach(item => {
      if (!employeeGroups[item.userId]) {
        employeeGroups[item.userId] = {
          employeeName: item.employeeName,
          department: item.department,
          payPeriods: 0,
          grossPay: 0,
          federalTax: 0,
          stateTax: 0,
          fica: 0,
          deductions: 0,
          netPay: 0
        };
      }
      employeeGroups[item.userId].payPeriods++;
      employeeGroups[item.userId].grossPay += item.grossPay || 0;
      employeeGroups[item.userId].federalTax += item.federalTax || 0;
      employeeGroups[item.userId].stateTax += item.stateTax || 0;
      employeeGroups[item.userId].fica += (item.socialSecurity || 0) + (item.medicare || 0);
      employeeGroups[item.userId].deductions += item.preTaxDeductions || 0;
      employeeGroups[item.userId].netPay += item.netPay || 0;
    });

    const employeeList = Object.values(employeeGroups).sort((a, b) => 
      a.employeeName.localeCompare(b.employeeName)
    );

    return {
      type: 'employee_earnings',
      title: 'Employee Earnings Report',
      employees: employeeList,
      totals: employeeList.reduce((acc, emp) => ({
        grossPay: acc.grossPay + emp.grossPay,
        federalTax: acc.federalTax + emp.federalTax,
        stateTax: acc.stateTax + emp.stateTax,
        fica: acc.fica + emp.fica,
        deductions: acc.deductions + emp.deductions,
        netPay: acc.netPay + emp.netPay
      }), { grossPay: 0, federalTax: 0, stateTax: 0, fica: 0, deductions: 0, netPay: 0 })
    };
  };

  const generateDepartmentSummary = (items) => {
    const deptGroups = {};
    items.forEach(item => {
      const dept = item.department || 'Unassigned';
      if (!deptGroups[dept]) {
        deptGroups[dept] = {
          department: dept,
          employeeCount: new Set(),
          grossPay: 0,
          taxes: 0,
          netPay: 0
        };
      }
      deptGroups[dept].employeeCount.add(item.userId);
      deptGroups[dept].grossPay += item.grossPay || 0;
      deptGroups[dept].taxes += (item.federalTax || 0) + (item.stateTax || 0) + 
                                (item.socialSecurity || 0) + (item.medicare || 0);
      deptGroups[dept].netPay += item.netPay || 0;
    });

    const deptList = Object.values(deptGroups).map(d => ({
      ...d,
      employeeCount: d.employeeCount.size
    })).sort((a, b) => b.grossPay - a.grossPay);

    return {
      type: 'department_summary',
      title: 'Department Summary Report',
      departments: deptList,
      totals: deptList.reduce((acc, d) => ({
        employeeCount: acc.employeeCount + d.employeeCount,
        grossPay: acc.grossPay + d.grossPay,
        taxes: acc.taxes + d.taxes,
        netPay: acc.netPay + d.netPay
      }), { employeeCount: 0, grossPay: 0, taxes: 0, netPay: 0 })
    };
  };

  const generateYTDSummary = (items) => {
    const monthlyData = {};
    items.forEach(item => {
      const date = new Date(item.createdAt);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          month: monthKey,
          employeeCount: new Set(),
          grossPay: 0,
          federalTax: 0,
          stateTax: 0,
          fica: 0,
          netPay: 0
        };
      }
      monthlyData[monthKey].employeeCount.add(item.userId);
      monthlyData[monthKey].grossPay += item.grossPay || 0;
      monthlyData[monthKey].federalTax += item.federalTax || 0;
      monthlyData[monthKey].stateTax += item.stateTax || 0;
      monthlyData[monthKey].fica += (item.socialSecurity || 0) + (item.medicare || 0);
      monthlyData[monthKey].netPay += item.netPay || 0;
    });

    const months = Object.values(monthlyData)
      .map(m => ({ ...m, employeeCount: m.employeeCount.size }))
      .sort((a, b) => a.month.localeCompare(b.month));

    return {
      type: 'ytd_summary',
      title: 'Year-to-Date Summary',
      months,
      totals: months.reduce((acc, m) => ({
        grossPay: acc.grossPay + m.grossPay,
        federalTax: acc.federalTax + m.federalTax,
        stateTax: acc.stateTax + m.stateTax,
        fica: acc.fica + m.fica,
        netPay: acc.netPay + m.netPay
      }), { grossPay: 0, federalTax: 0, stateTax: 0, fica: 0, netPay: 0 })
    };
  };

  const generateQuarterly941 = (items) => {
    // Form 941 quarterly report data
    const grossWages = items.reduce((sum, item) => sum + (item.grossPay || 0), 0);
    const federalWithheld = items.reduce((sum, item) => sum + (item.federalTax || 0), 0);
    const ssWages = items.reduce((sum, item) => sum + Math.min(item.grossPay || 0, 168600), 0);
    const ssTax = items.reduce((sum, item) => sum + (item.socialSecurity || 0), 0);
    const medicareWages = grossWages;
    const medicareTax = items.reduce((sum, item) => sum + (item.medicare || 0), 0);
    const employeeCount = new Set(items.map(i => i.userId)).size;

    return {
      type: 'quarterly_941',
      title: 'Form 941 - Quarterly Federal Tax Return',
      quarter: selectedQuarter,
      year: new Date(dateRange.start).getFullYear(),
      data: {
        // Line 1 - Number of employees
        line1_employeeCount: employeeCount,
        // Line 2 - Wages, tips, other compensation
        line2_wages: grossWages,
        // Line 3 - Federal income tax withheld
        line3_federalWithheld: federalWithheld,
        // Line 5a - Taxable social security wages
        line5a_ssWages: ssWages,
        line5a_ssTax: ssTax * 2, // Employee + Employer
        // Line 5c - Taxable Medicare wages
        line5c_medicareWages: medicareWages,
        line5c_medicareTax: medicareTax * 2, // Employee + Employer
        // Line 6 - Total taxes before adjustments
        line6_totalTaxes: federalWithheld + (ssTax * 2) + (medicareTax * 2),
        // Line 10 - Total taxes after adjustments
        line10_totalTaxes: federalWithheld + (ssTax * 2) + (medicareTax * 2),
        // Line 12 - Total deposits for quarter
        line12_deposits: 0, // Would come from tax payments
        // Line 14 - Balance due or overpayment
        line14_balance: federalWithheld + (ssTax * 2) + (medicareTax * 2)
      }
    };
  };

  const handleExportCSV = () => {
    if (!reportData) return;
    
    try {
      let csvData = [];
      
      switch (reportData.type) {
        case 'payroll_register':
          csvData = reportData.items.map(item => ({
            'Employee': item.employeeName,
            'Department': item.department,
            'Gross Pay': item.grossPay?.toFixed(2),
            'Federal Tax': item.federalTax?.toFixed(2),
            'State Tax': item.stateTax?.toFixed(2),
            'Social Security': item.socialSecurity?.toFixed(2),
            'Medicare': item.medicare?.toFixed(2),
            'Pre-Tax Deductions': item.preTaxDeductions?.toFixed(2),
            'Net Pay': item.netPay?.toFixed(2)
          }));
          break;
        case 'employee_earnings':
          csvData = reportData.employees.map(emp => ({
            'Employee': emp.employeeName,
            'Department': emp.department,
            'Pay Periods': emp.payPeriods,
            'Gross Pay': emp.grossPay?.toFixed(2),
            'Federal Tax': emp.federalTax?.toFixed(2),
            'State Tax': emp.stateTax?.toFixed(2),
            'FICA': emp.fica?.toFixed(2),
            'Deductions': emp.deductions?.toFixed(2),
            'Net Pay': emp.netPay?.toFixed(2)
          }));
          break;
        default:
          toast.error('Export not available for this report type');
          return;
      }

      exportToCSV(csvData, `${reportData.type}_${dateRange.start}_${dateRange.end}`);
      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">US Payroll Reports</h1>
          <p className="text-gray-500 mt-1">Federal and state tax reports for US payroll</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportCSV} disabled={!reportData}>
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button variant="outline" disabled={!reportData}>
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label>Report Type</Label>
              <Select value={selectedReport} onValueChange={setSelectedReport}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {US_REPORT_TYPES.map(report => (
                    <SelectItem key={report.id} value={report.id}>
                      {report.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
              />
            </div>

            {selectedReport === 'quarterly_941' ? (
              <div className="space-y-2">
                <Label>Quarter</Label>
                <Select value={selectedQuarter} onValueChange={setSelectedQuarter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Q1 (Jan-Mar)</SelectItem>
                    <SelectItem value="2">Q2 (Apr-Jun)</SelectItem>
                    <SelectItem value="3">Q3 (Jul-Sep)</SelectItem>
                    <SelectItem value="4">Q4 (Oct-Dec)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Department</Label>
                <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map(dept => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Pay Group</Label>
              <Select value={selectedPayGroup} onValueChange={setSelectedPayGroup}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Pay Groups</SelectItem>
                  {payGroups.map(group => (
                    <SelectItem key={group.id} value={group.id}>{group.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Content */}
      {generating ? (
        <Card>
          <CardContent className="py-12 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-teal-600 mb-4" />
            <p className="text-gray-500">Generating report...</p>
          </CardContent>
        </Card>
      ) : reportData ? (
        <Card>
          <CardHeader>
            <CardTitle>{reportData.title}</CardTitle>
            <CardDescription>
              {formatDate(new Date(dateRange.start))} - {formatDate(new Date(dateRange.end))}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Payroll Register */}
            {reportData.type === 'payroll_register' && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Fed Tax</TableHead>
                    <TableHead className="text-right">State Tax</TableHead>
                    <TableHead className="text-right">SS/Med</TableHead>
                    <TableHead className="text-right">Deductions</TableHead>
                    <TableHead className="text-right">Net Pay</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportData.items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                        No payroll data found for the selected period
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {reportData.items.map((item, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{item.employeeName}</TableCell>
                          <TableCell>{item.department}</TableCell>
                          <TableCell className="text-right">{formatUSDCurrency(item.grossPay)}</TableCell>
                          <TableCell className="text-right text-red-600">{formatUSDCurrency(item.federalTax)}</TableCell>
                          <TableCell className="text-right text-red-600">{formatUSDCurrency(item.stateTax)}</TableCell>
                          <TableCell className="text-right text-red-600">
                            {formatUSDCurrency((item.socialSecurity || 0) + (item.medicare || 0))}
                          </TableCell>
                          <TableCell className="text-right text-amber-600">{formatUSDCurrency(item.preTaxDeductions)}</TableCell>
                          <TableCell className="text-right font-bold text-emerald-600">{formatUSDCurrency(item.netPay)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-gray-50 font-bold">
                        <TableCell colSpan={2}>TOTALS</TableCell>
                        <TableCell className="text-right">{formatUSDCurrency(reportData.totals.grossPay)}</TableCell>
                        <TableCell className="text-right text-red-600">{formatUSDCurrency(reportData.totals.federalTax)}</TableCell>
                        <TableCell className="text-right text-red-600">{formatUSDCurrency(reportData.totals.stateTax)}</TableCell>
                        <TableCell className="text-right text-red-600">
                          {formatUSDCurrency(reportData.totals.socialSecurity + reportData.totals.medicare)}
                        </TableCell>
                        <TableCell className="text-right text-amber-600">{formatUSDCurrency(reportData.totals.preTaxDeductions)}</TableCell>
                        <TableCell className="text-right text-emerald-600">{formatUSDCurrency(reportData.totals.netPay)}</TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            )}

            {/* Tax Summary */}
            {reportData.type === 'tax_summary' && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-blue-600" />
                    Federal Taxes (Form 941)
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">Gross Wages</p>
                      <p className="text-xl font-bold">{formatUSDCurrency(reportData.federal.grossWages)}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">Federal Tax Withheld</p>
                      <p className="text-xl font-bold text-red-600">{formatUSDCurrency(reportData.federal.incomeTax)}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">FICA (EE + ER)</p>
                      <p className="text-xl font-bold">{formatUSDCurrency(reportData.federal.totalFICA * 2)}</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-600">Total 941 Liability</p>
                      <p className="text-xl font-bold text-blue-700">{formatUSDCurrency(reportData.federal.total941Liability)}</p>
                    </div>
                  </div>
                </div>

                {reportData.states.length > 0 && (
                  <div>
                    <h3 className="font-medium mb-3">State Taxes</h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>State</TableHead>
                          <TableHead className="text-right">Employees</TableHead>
                          <TableHead className="text-right">Taxable Wages</TableHead>
                          <TableHead className="text-right">Tax Withheld</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reportData.states.map((state, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-medium">{state.stateName}</TableCell>
                            <TableCell className="text-right">{state.employees}</TableCell>
                            <TableCell className="text-right">{formatUSDCurrency(state.wages)}</TableCell>
                            <TableCell className="text-right text-red-600">{formatUSDCurrency(state.tax)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}

            {/* Employee Earnings */}
            {reportData.type === 'employee_earnings' && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead className="text-right">Periods</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Fed Tax</TableHead>
                    <TableHead className="text-right">State Tax</TableHead>
                    <TableHead className="text-right">FICA</TableHead>
                    <TableHead className="text-right">Net Pay</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportData.employees.map((emp, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{emp.employeeName}</TableCell>
                      <TableCell>{emp.department}</TableCell>
                      <TableCell className="text-right">{emp.payPeriods}</TableCell>
                      <TableCell className="text-right">{formatUSDCurrency(emp.grossPay)}</TableCell>
                      <TableCell className="text-right text-red-600">{formatUSDCurrency(emp.federalTax)}</TableCell>
                      <TableCell className="text-right text-red-600">{formatUSDCurrency(emp.stateTax)}</TableCell>
                      <TableCell className="text-right text-red-600">{formatUSDCurrency(emp.fica)}</TableCell>
                      <TableCell className="text-right font-bold text-emerald-600">{formatUSDCurrency(emp.netPay)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-gray-50 font-bold">
                    <TableCell colSpan={3}>TOTALS</TableCell>
                    <TableCell className="text-right">{formatUSDCurrency(reportData.totals.grossPay)}</TableCell>
                    <TableCell className="text-right text-red-600">{formatUSDCurrency(reportData.totals.federalTax)}</TableCell>
                    <TableCell className="text-right text-red-600">{formatUSDCurrency(reportData.totals.stateTax)}</TableCell>
                    <TableCell className="text-right text-red-600">{formatUSDCurrency(reportData.totals.fica)}</TableCell>
                    <TableCell className="text-right text-emerald-600">{formatUSDCurrency(reportData.totals.netPay)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}

            {/* Department Summary */}
            {reportData.type === 'department_summary' && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Department</TableHead>
                    <TableHead className="text-right">Employees</TableHead>
                    <TableHead className="text-right">Gross Pay</TableHead>
                    <TableHead className="text-right">Taxes</TableHead>
                    <TableHead className="text-right">Net Pay</TableHead>
                    <TableHead className="text-right">% of Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportData.departments.map((dept, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{dept.department}</TableCell>
                      <TableCell className="text-right">{dept.employeeCount}</TableCell>
                      <TableCell className="text-right">{formatUSDCurrency(dept.grossPay)}</TableCell>
                      <TableCell className="text-right text-red-600">{formatUSDCurrency(dept.taxes)}</TableCell>
                      <TableCell className="text-right text-emerald-600">{formatUSDCurrency(dept.netPay)}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline">
                          {reportData.totals.grossPay > 0 
                            ? ((dept.grossPay / reportData.totals.grossPay) * 100).toFixed(1)
                            : 0}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-gray-50 font-bold">
                    <TableCell>TOTALS</TableCell>
                    <TableCell className="text-right">{reportData.totals.employeeCount}</TableCell>
                    <TableCell className="text-right">{formatUSDCurrency(reportData.totals.grossPay)}</TableCell>
                    <TableCell className="text-right text-red-600">{formatUSDCurrency(reportData.totals.taxes)}</TableCell>
                    <TableCell className="text-right text-emerald-600">{formatUSDCurrency(reportData.totals.netPay)}</TableCell>
                    <TableCell className="text-right">100%</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}

            {/* YTD Summary */}
            {reportData.type === 'ytd_summary' && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead className="text-right">Employees</TableHead>
                    <TableHead className="text-right">Gross Pay</TableHead>
                    <TableHead className="text-right">Fed Tax</TableHead>
                    <TableHead className="text-right">State Tax</TableHead>
                    <TableHead className="text-right">FICA</TableHead>
                    <TableHead className="text-right">Net Pay</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportData.months.map((month, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">
                        {new Date(month.month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </TableCell>
                      <TableCell className="text-right">{month.employeeCount}</TableCell>
                      <TableCell className="text-right">{formatUSDCurrency(month.grossPay)}</TableCell>
                      <TableCell className="text-right text-red-600">{formatUSDCurrency(month.federalTax)}</TableCell>
                      <TableCell className="text-right text-red-600">{formatUSDCurrency(month.stateTax)}</TableCell>
                      <TableCell className="text-right text-red-600">{formatUSDCurrency(month.fica)}</TableCell>
                      <TableCell className="text-right text-emerald-600">{formatUSDCurrency(month.netPay)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-gray-50 font-bold">
                    <TableCell>YTD TOTALS</TableCell>
                    <TableCell className="text-right">-</TableCell>
                    <TableCell className="text-right">{formatUSDCurrency(reportData.totals.grossPay)}</TableCell>
                    <TableCell className="text-right text-red-600">{formatUSDCurrency(reportData.totals.federalTax)}</TableCell>
                    <TableCell className="text-right text-red-600">{formatUSDCurrency(reportData.totals.stateTax)}</TableCell>
                    <TableCell className="text-right text-red-600">{formatUSDCurrency(reportData.totals.fica)}</TableCell>
                    <TableCell className="text-right text-emerald-600">{formatUSDCurrency(reportData.totals.netPay)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}

            {/* Form 941 */}
            {reportData.type === 'quarterly_941' && (
              <div className="space-y-6">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-lg font-bold">Form 941</h3>
                  <p className="text-gray-600">Employer's QUARTERLY Federal Tax Return</p>
                  <p className="text-sm text-gray-500">Quarter {reportData.quarter}, {reportData.year}</p>
                </div>

                <div className="space-y-4 border rounded-lg p-4">
                  <div className="flex justify-between items-center py-2 border-b">
                    <span>1. Number of employees who received wages</span>
                    <span className="font-bold">{reportData.data.line1_employeeCount}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span>2. Wages, tips, and other compensation</span>
                    <span className="font-bold">{formatUSDCurrency(reportData.data.line2_wages)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span>3. Federal income tax withheld</span>
                    <span className="font-bold text-red-600">{formatUSDCurrency(reportData.data.line3_federalWithheld)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span>5a. Taxable social security wages × 0.124</span>
                    <div className="text-right">
                      <span className="text-gray-500 text-sm">{formatUSDCurrency(reportData.data.line5a_ssWages)} × 12.4%</span>
                      <span className="font-bold block">{formatUSDCurrency(reportData.data.line5a_ssTax)}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span>5c. Taxable Medicare wages × 0.029</span>
                    <div className="text-right">
                      <span className="text-gray-500 text-sm">{formatUSDCurrency(reportData.data.line5c_medicareWages)} × 2.9%</span>
                      <span className="font-bold block">{formatUSDCurrency(reportData.data.line5c_medicareTax)}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b bg-gray-50 px-2 -mx-2">
                    <span className="font-medium">6. Total taxes before adjustments</span>
                    <span className="font-bold">{formatUSDCurrency(reportData.data.line6_totalTaxes)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b bg-blue-50 px-2 -mx-2">
                    <span className="font-medium">10. Total taxes after adjustments</span>
                    <span className="font-bold text-blue-700">{formatUSDCurrency(reportData.data.line10_totalTaxes)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span>12. Total deposits for this quarter</span>
                    <span className="font-bold">{formatUSDCurrency(reportData.data.line12_deposits)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 bg-amber-50 px-2 -mx-2">
                    <span className="font-medium">14. Balance due</span>
                    <span className="font-bold text-amber-700">{formatUSDCurrency(reportData.data.line14_balance)}</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Select a report type and date range to generate</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
